import { ref, watch, nextTick, type Ref } from "vue";
import { useVueFlow } from "@vue-flow/core";
import { getLayoutedElements, type FlowDirection } from "@/composables/dagre-layout";
import type { RunCanvasNode, RunCanvasEdge } from "@/types";

/**
 * The generic canvas mechanics — dagre re-layout, flow direction, viewport, and
 * session-only drag — for a read-only run canvas.
 *
 * This is the half of `useWorkflowCanvas` that isn't tangled up in the editor's
 * mode-transition lifecycle. There is no `setCanvasMode`, no per-workflow
 * `nodePositions` persistence map, and no saved-position round-trip: a run is
 * read-only, and now also *live*, so persisting drags would only fight the
 * frames that redraw it. Drag is allowed, but for the session only.
 *
 * Node-position ownership: the composable holds a local `canvasNodes` copy of
 * the source (laid-out) history nodes so LR/fit/drag don't mutate the shared
 * `historyNodes` computed. Live frames reconcile against it:
 *   - Same node ids (a status/duration changed) → merge `data` in place, keeping
 *     positions so a finishing service doesn't jump.
 *   - Different node ids (new run, or structure changed) → re-sync and re-lay-out.
 *
 * @param sourceNodes Laid-out history nodes from the derivation (positions set).
 * @param sourceEdges Structural edges (source/target) driving the layout.
 * @param instanceId  Explicit VueFlow store id. Required so the canvas gets its
 *   OWN store: `useVueFlow()` with no id injects the nearest provided instance,
 *   and in the builder RunCanvas mounts *inside* the editor's VueFlow provider —
 *   without an explicit id the two would share one store and fight over
 *   viewport / fitView / nodes. RunCanvas passes the same id to its <VueFlow>.
 */
export function useRunCanvasLayout(
  sourceNodes: Ref<RunCanvasNode[]>,
  sourceEdges: Ref<RunCanvasEdge[]>,
  instanceId: string,
  runId: Ref<string>
) {
  const { zoomIn, zoomOut, fitView, setViewport, onNodesInitialized } = useVueFlow(instanceId);

  const flowDirection = ref<FlowDirection>("TB");
  const canvasNodes = ref<RunCanvasNode[]>([]);

  // The run whose positions canvasNodes currently holds. A different run reaching the watch means take
  // its positions afresh (each run carries its own snapshotted layout); the same run reaching it again is
  // a live frame, which keeps positions. Without this, two runs of one workflow — same service ids — take
  // the "merge data, keep positions" branch and the second run wears the first one's (or a drag's) layout.
  let syncedRunId = "";

  // Default fit for every "settle the graph into view" call — mount, re-layout,
  // direction toggle. Padding sits the graph a step further out than a tight fit,
  // and maxZoom stops a small run (few nodes) from being blown up past this size;
  // a large run is already below the cap, so it fits as before. One source of truth
  // so the initial view and the fit button land on the same zoom.
  const FIT_OPTIONS = { padding: 0.2, maxZoom: 0.85 } as const;

  // Fit once the nodes have real measured dimensions. `pane-ready` fires when the
  // VueFlow pane mounts — which, on first showing a run, is BEFORE the nodes are
  // measured, so a fitView there computes against zero-size nodes and no-ops,
  // leaving the default viewport (top-left, zoom 1). onNodesInitialized is the
  // dimensions-ready signal, so fitting here is what actually centers the run.
  onNodesInitialized(() => {
    fitView(FIT_OPTIONS);
  });

  function idsOf(nodes: RunCanvasNode[]): string {
    // Order-independent structural key: same set of node ids → same string.
    return nodes
      .map((n) => n.id)
      .sort()
      .join("|");
  }

  function layout(nodes: RunCanvasNode[]): RunCanvasNode[] {
    return getLayoutedElements(nodes, sourceEdges.value, flowDirection.value).nodes;
  }

  // Reconcile the local copy whenever the source nodes change. The source is
  // already laid out (TB) at derivation time; we take fresh positions when the run
  // changes or its structure does, and otherwise merge data in place.
  watch(
    [sourceNodes, runId] as const,
    ([next, currentRunId]) => {
      // A different run selected, or a service was added/removed: take fresh positions in the current
      // direction (source is already TB; LR needs a dagre pass). The run-id check is what stops a second
      // run of the same workflow — identical service ids — from inheriting the first run's positions.
      const runChanged = currentRunId !== syncedRunId;
      const structuralChange = idsOf(next) !== idsOf(canvasNodes.value);
      if (runChanged || structuralChange) {
        canvasNodes.value =
          flowDirection.value === "TB" ? next.map((n) => ({ ...n })) : layout(next);
        syncedRunId = currentRunId;
        return;
      }
      // Same run, same node ids — a live frame only updated a status/duration. Merge the new data onto
      // the existing nodes and keep their positions, so a finishing service (and any node the user
      // dragged) doesn't jump. Direction-independent: in LR this is what preserves session drags across
      // frames.
      const byId = new Map(next.map((n) => [n.id, n]));
      canvasNodes.value = canvasNodes.value.map((node) => {
        const fresh = byId.get(node.id);
        return fresh ? { ...node, data: fresh.data } : node;
      });
    },
    { immediate: true, deep: false }
  );

  // Re-run dagre over the current nodes in the current direction (the "auto layout"
  // button). Returns nothing — it writes the local copy the canvas binds to.
  function applyLayout() {
    canvasNodes.value = layout(canvasNodes.value);
    nextTick(() => fitView(FIT_OPTIONS));
  }

  function toggleFlowDirection() {
    flowDirection.value = flowDirection.value === "TB" ? "LR" : "TB";
    applyLayout();
  }

  // Session-only drag: keep the dropped position on the local copy so a live frame
  // (which merges data, not position) doesn't snap it back.
  function onNodeDragStop(event: { node: { id: string; position: { x: number; y: number } } }) {
    const { node } = event;
    canvasNodes.value = canvasNodes.value.map((n) =>
      n.id === node.id ? { ...n, position: { ...node.position } } : n
    );
  }

  // Fit the freshly-mounted graph into view. VueFlow remounts (keyed on runId +
  // flowDirection) fire pane-ready, which calls this.
  function restoreViewport() {
    nextTick(() => fitView(FIT_OPTIONS));
  }

  return {
    // state the canvas binds to
    canvasNodes,
    flowDirection,
    // controls
    zoomIn,
    zoomOut,
    fitView,
    setViewport,
    applyLayout,
    toggleFlowDirection,
    onNodeDragStop,
    restoreViewport,
  };
}
